<template>
  <div>
    <h5>CD Poster Performance Report</h5>

    <b-card class="mb-4 report-filters-card">
      <b-form inline class="report-filters" @submit.prevent="fetchReport">
        <ReportPeriodFilter
          :period-type.sync="periodType"
          :time-period.sync="timePeriod"
          :selected-dates.sync="selectedDates"
          :time-options="timeOptions"
          :date-config="dateConfig"
          radio-name="cdPosterPeriodType"
        />

        <button class="btn btn-primary">Show Report</button>
      </b-form>
      <small class="text-muted d-block mt-3 report-description">
        Orders are grouped by the user who originally posted them to Central Dispatch. The selected period is based on the original CD posting date.
      </small>
    </b-card>

    <LoadingIndicator v-if="loading" centered />
    <span v-else-if="empty">No records found</span>

    <b-card v-else-if="items.length" class="mb-4 report-results" no-body>
      <b-table responsive striped hover small :items="items" :fields="fields" class="mb-0" show-empty>
        <template #table-caption> CD postings from {{ periodLabel }} </template>

        <template #cell(posterUserId)="data">
          <b-link v-if="data.value" :to="userLink(data.value)" target="_blank">{{ data.item.user ? data.item.user.name : `#${data.value}` }}</b-link>
        </template>

        <template #cell(showDetails)="row">
          <b-button size="xs" variant="secondary" @click.prevent="toggleDetails(row)">
            {{ row.detailsShowing ? 'Hide' : 'Zoom' }}
          </b-button>
        </template>

        <template #row-details="row">
          <div class="details-wrapper">
            <LoadingIndicator v-if="row.item.loading" centered />
            <b-table v-else responsive striped hover small :items="row.item.rows" :fields="detailsFields" class="mb-0">
              <template #cell(id)="data">
                <b-link :to="orderLink(data.item.id)" target="_blank">{{ data.value }}</b-link>
              </template>
              <template #cell(cdPostedAt)="data">{{ data.value | formatDateTime }}</template>
              <template #cell(signedAt)="data">
                <span v-if="data.value">{{ data.value | formatDateTime }}</span>
                <span v-else>-</span>
              </template>
              <template #cell(deletedAt)="data">{{ data.value ? 'Yes' : 'No' }}</template>
            </b-table>
          </div>
        </template>

        <template #custom-foot="data">
          <b-tr class="font-weight-bold">
            <b-th v-for="field in data.fields" :key="field.key">
              {{ field.key === 'posterUserId' ? 'Totals' : formatTotal(field.key) }}
            </b-th>
          </b-tr>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import moment from 'moment';
import ReportPeriodFilter from '@/components/pages/reports/ReportPeriodFilter';
import { ApiClient } from '@/api/ApiClient';
import LoadingIndicator from '@/components/shared/LoadingIndicator';
import { tableFields } from '@/mixins/methods';

export default {
  name: 'ReportCdPosterPerformance',
  mixins: [tableFields],
  components: { LoadingIndicator, ReportPeriodFilter },
  data() {
    const currency = value => (value === null || value === undefined ? '-' : `$${Number(value).toLocaleString()}`);
    const percentage = value => (value === null || value === undefined ? '-' : `${value}%`);
    const unchanged = value => value;
    return {
      loading: false,
      empty: false,
      items: [],
      totals: {},
      periodType: 'list',
      timePeriod: 1,
      selectedDates: null,
      form: { postedStart: null, postedEnd: null },
      dateConfig: { mode: 'range', dateFormat: 'Y-m-d', altInput: true, altFormat: 'm/d/Y', allowInput: true },
      timeOptions: [
        { text: 'Current month', value: 1 },
        { text: 'Last month', value: 2 },
        { text: 'Current quarter', value: 3 },
        { text: 'Last quarter', value: 4 },
        { text: 'Current year', value: 5 },
        { text: 'All time', value: 6 },
      ],
      fields: [
        { key: 'posterUserId', label: 'CD Poster' },
        { key: 'totalPosted', label: 'Total Posted', sortable: true },
        { key: 'averageBrokerFee', label: 'Avg Broker Fee', sortable: true, formatter: currency },
        { key: 'canceledCount', label: 'Canceled', sortable: true },
        { key: 'cancellationRatio', label: 'Cancel %', sortable: true, formatter: percentage },
        { key: 'dispatchedCount', label: 'Dispatched', sortable: true },
        { key: 'dispatchRatio', label: 'Dispatch %', sortable: true, formatter: percentage },
        { key: 'dispatchedBeforePuw', label: 'Before PUW', sortable: true },
        { key: 'dispatchedDuringPuw', label: 'During PUW', sortable: true },
        { key: 'stillPostedAfterPuw', label: 'Still Posted After PUW', sortable: true },
        { key: 'visibilitiesPosted', label: 'Visibilities Posted', sortable: true },
        { key: 'issuesCount', label: 'Issues', sortable: true },
        { key: 'archivedCount', label: 'Archived', sortable: true },
        { key: 'approvalsCount', label: 'Approvals', sortable: true },
        { key: 'showDetails', label: '' },
      ],
      detailsFields: [
        { key: 'id', label: 'Order', sortable: true },
        { key: 'cdPostedAt', label: 'CD Posted At', sortable: true, formatter: unchanged },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'outcome', label: 'Outcome', sortable: true },
        { key: 'moveDate', label: 'PUW Start', sortable: true },
        { key: 'moveDateLast', label: 'PUW End', sortable: true },
        { key: 'signedAt', label: 'Dispatched At', sortable: true, formatter: unchanged },
        { key: 'tariff', label: 'Tariff', sortable: true, formatter: currency },
        { key: 'carrierPay', label: 'Carrier Pay', sortable: true, formatter: currency },
        { key: 'brokerFee', label: 'Broker Fee', sortable: true, formatter: currency },
        { key: 'visibilitiesPosted', label: 'Visibilities Posted', sortable: true },
        { key: 'approvalsCount', label: 'Approvals', sortable: true },
        { key: 'deletedAt', label: 'Archived', sortable: true },
      ],
    };
  },
  computed: {
    periodLabel() {
      if (!this.form.postedStart) return 'all time';
      return `${this.form.postedStart} through ${this.form.postedEnd}`;
    },
  },
  methods: {
    prepareDates() {
      let dates = [];
      if (this.periodType === 'custom') {
        if (!this.selectedDates) return false;
        dates = this.selectedDates.split(' to ');
        if (dates.length < 2) dates.push(dates[0]);
      } else if (this.timePeriod === 1) dates = [moment().startOf('month'), moment().endOf('month')];
      else if (this.timePeriod === 2) dates = [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')];
      else if (this.timePeriod === 3) dates = [moment().startOf('quarter'), moment().endOf('quarter')];
      else if (this.timePeriod === 4) dates = [moment().subtract(1, 'quarter').startOf('quarter'), moment().subtract(1, 'quarter').endOf('quarter')];
      else if (this.timePeriod === 5) dates = [moment().startOf('year'), moment().endOf('year')];

      this.form.postedStart = dates[0] ? moment(dates[0]).format('YYYY-MM-DD') : null;
      this.form.postedEnd = dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : null;
      return true;
    },
    async fetchReport() {
      if (!this.prepareDates()) return;
      this.loading = true;
      try {
        const response = await ApiClient.reports.cdPosterPerformance({ params: this.form });
        this.items = response.data.data.rows.map(item => ({ ...item, rows: [], loading: false }));
        this.totals = response.data.data.totals;
        this.empty = !this.items.length;
      } finally {
        this.loading = false;
      }
    },
    async toggleDetails(row) {
      row.toggleDetails();
      if (!row.item._showDetails || row.item.rows.length) return;
      row.item.loading = true;
      try {
        const params = { ...this.form, posterUserId: row.item.posterUserId };
        const response = await ApiClient.reports.cdPosterPerformanceDetails({ params });
        row.item.rows = response.data.data.rows;
      } finally {
        row.item.loading = false;
      }
    },
    userLink(id) {
      return { name: 'UserDetails', params: { id } };
    },
    orderLink(id) {
      return { name: 'OrderDetails', params: { id } };
    },
    formatTotal(key) {
      if (key === 'showDetails') return '';
      const value = this.totals[key];
      if (['averageBrokerFee'].includes(key)) return value === null || value === undefined ? '-' : `$${Number(value).toLocaleString()}`;
      if (['cancellationRatio', 'dispatchRatio'].includes(key)) return value === null || value === undefined ? '-' : `${value}%`;
      return this.formatReportValue(key, value);
    },
  },
};
</script>

<style lang="scss" scoped>
.report-results ::v-deep table {
  white-space: nowrap;
}

.details-wrapper {
  padding: 1rem;
  background: rgba(0, 0, 0, 0.025);
}

.report-filters {
  justify-content: flex-start;
  align-items: center;
  gap: 0.75rem;
}

.report-filters .btn {
  height: 38px;
}

.report-description {
  width: 100%;
}

@media (max-width: 767.98px) {
  .report-filters {
    justify-content: flex-start;
    gap: 0.5rem;
  }

  .report-filters .btn {
    width: 100%;
  }
}
</style>
